<template>
  <div class="row">
    <!-- Statistiques globales -->
    <div class="col-lg-3 col-sm-6 mb-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">
          <div class="d-flex align-items-center justify-content-between">
            <div class="flex-shrink-0">
              <i class="flaticon-users text-primary" style="font-size: 40px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 30px;">
                {{ stats.totalAgents }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">Agents actifs</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg-3 col-sm-6 mb-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">
          <div class="d-flex align-items-center justify-content-between">
            <div class="flex-shrink-0">
              <i class="flaticon-folder text-warning" style="font-size: 40px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 30px;">
                {{ stats.totalQueues }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">Files de validation</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg-3 col-sm-6 mb-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">
          <div class="d-flex align-items-center justify-content-between">
            <div class="flex-shrink-0">
              <i class="flaticon-time text-info" style="font-size: 40px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 30px;">
                {{ stats.pendingValidation }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">En attente validation</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-lg-3 col-sm-6 mb-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30">
          <div class="d-flex align-items-center justify-content-between">
            <div class="flex-shrink-0">
              <i class="flaticon-check text-success" style="font-size: 40px;"></i>
            </div>
            <div class="flex-grow-1 ms-3">
              <span class="count fw-bold text-black d-block mb-0" style="font-size: 30px;">
                {{ stats.validatedToday }}
              </span>
              <p class="text-body-tertiary fs-12 fw-medium mb-0">Validés aujourd'hui</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Graphique de performance -->
    <div class="col-lg-8 mb-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
          <h4 class="fw-bold text-black mb-0">Performance des Agents</h4>
          <div style="width: 200px;">
            <Multiselect
              v-model="selectedPeriod"
              :options="periodOptions"
              :searchable="false"
              :close-on-select="true"
              placeholder="Sélectionner une période"
              @change="loadPerformanceData"
            />
          </div>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="performance-chart">
            <canvas ref="performanceChart" width="400" height="200"></canvas>
          </div>
        </div>
      </div>
    </div>

    <!-- Alertes et notifications -->
    <div class="col-lg-4 mb-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-head box-shadow bg-white p-15 p-sm-20 p-md-25">
          <h4 class="fw-bold text-black mb-0">Alertes</h4>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div v-if="alerts.length === 0" class="text-center py-3">
            <i class="flaticon-check-circle text-success" style="font-size: 48px;"></i>
            <p class="text-muted mt-2">Aucune alerte</p>
          </div>
          <div v-else class="alerts-list">
            <div v-for="alert in alerts" :key="alert.id" class="alert-item" :class="getAlertClass(alert.type)">
              <div class="d-flex align-items-start">
                <i :class="getAlertIcon(alert.type)" class="me-2 mt-1"></i>
                <div class="flex-grow-1">
                  <div class="fw-medium">{{ alert.title }}</div>
                  <div class="small text-muted">{{ alert.message }}</div>
                  <div class="small text-muted">{{ formatDate(alert.created_at) }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Liste des agents avec performance -->
    <div class="col-12 mb-25">
      <div class="card border-0 rounded-0 bg-white">
        <div class="card-head box-shadow bg-white d-lg-flex align-items-center justify-content-between p-15 p-sm-20 p-md-25">
          <h4 class="fw-bold text-black mb-0">Performance des Agents</h4>
          <div class="d-flex align-items-center gap-2">
            <small class="text-muted">Période: {{ selectedPeriod }} jours</small>
            <button @click="loadSupervisorData" class="btn btn-primary btn-sm">
              <i class="flaticon-refresh me-1"></i> Actualiser
            </button>
          </div>
        </div>
        <div class="card-body p-15 p-sm-20 p-md-25">
          <div class="table-responsive">
            <table class="table align-middle mb-0">
              <thead>
                <tr>
                  <th>Agent</th>
                  <th>Files assignées</th>
                  <th>Tâches génériques (en cours)</th>
                  <th>Dossiers spécifiques (en cours)</th>
                  <th>Dossiers complétés</th>
                  <th>Tâches génériques complétées</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="agent in agentPerformances" :key="agent.id">
                  <td>
                    <div class="d-flex align-items-center">
                      <div class="agent-avatar me-2">
                        <i class="flaticon-user-1"></i>
                      </div>
                      <div>
                        <div class="fw-medium">{{ agent.nom }} {{ agent.prenom }}</div>
                        <div class="small text-muted">{{ agent.email }}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span class="badge bg-primary">{{ agent.assignedQueues }}</span>
                  </td>
                   <td>
                        <span class="badge bg-warning">{{ agent.genericTasksInProgress }}</span>
                      </td>
                      <td>
                        <span class="badge bg-info">{{ agent.specificDossiersInProgress }}</span>
                      </td>
                      <td>
                        <span class="badge bg-success">{{ agent.completedDossiers }}</span>
                      </td>
                      <td>
                        <span class="badge bg-success">{{ agent.completedGenericTasks }}</span>
                      </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import ApiService from '@/services/ApiService';
import JwtService from '@/services/JwtService';
import { error, success } from '@/utils/utils';
import Multiselect from '@vueform/multiselect';
import { Chart, registerables } from 'chart.js'; 

Chart.register(...registerables); 

interface SupervisorStats {
  totalAgents: number;
  totalQueues: number;
  pendingValidation: number;
  validatedToday: number;
}

interface AgentPerformance {
  id: string;
  nom: string;
  prenom: string;
  email: string;
  assignedQueues: number;
  genericTasksInProgress: number;      
  specificDossiersInProgress: number; 
  completedDossiers: number;           
  completedGenericTasks: number;  
}

interface Alert {
  id: string;
  type: string;
  title: string;
  message: string;
  created_at: string;
}

export default defineComponent({
  name: 'SuperviseurDashboard',
  components: {
    Multiselect
  },
  setup() {
    const loading = ref(false);
    const selectedPeriod = ref('7');
    const performanceChart = ref<HTMLCanvasElement | null>(null);
    let chartInstance: Chart | null = null; // 👈 Stocker l'instance du graphique
    
    const periodOptions = [
      { value: '7', label: '7 derniers jours' },
      { value: '30', label: '30 derniers jours' },
      { value: '90', label: '3 derniers mois' }
    ];
    
    const stats = ref<SupervisorStats>({
      totalAgents: 0,
      totalQueues: 0,
      pendingValidation: 0,
      validatedToday: 0
    });
    
    const agentPerformances = ref<AgentPerformance[]>([]);
    const alerts = ref<Alert[]>([]);

    const loadSupervisorData = async () => {
      loading.value = true;
      try {
        const user = JwtService.getUser();
        if (!user) {
          error("Utilisateur non connecté");
          return;
        }

        const statsResponse = await ApiService.get('/supervisor/stats');
        stats.value = statsResponse.data.data;

        const performanceResponse = await ApiService.get(`/supervisor/agent-performances?period=${selectedPeriod.value}`);
        agentPerformances.value = performanceResponse.data.data;
        console.log("agentPerformances", performanceResponse);

        const alertsResponse = await ApiService.get('/supervisor/alerts?limit=5');
        alerts.value = alertsResponse.data.data;

      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors du chargement des données');
      } finally {
        loading.value = false;
      }
    };

    const loadPerformanceData = async () => {
      try {
        const response = await ApiService.get(`/supervisor/performance-chart?period=${selectedPeriod.value}`);
        const data = response.data.data;

        console.log('📊 Données du graphique:', data);

        // Détruire l'ancien graphique s'il existe
        if (chartInstance) {
          chartInstance.destroy();
        }

        // Créer le nouveau graphique
        if (performanceChart.value) {
          const ctx = performanceChart.value.getContext('2d');
          if (ctx) {
            chartInstance = new Chart(ctx, {
              type: 'line',
              data: {
                labels: data.map((d: any) => {
                  const date = new Date(d.date);
                  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
                }),
                datasets: [
                  {
                    label: 'Dossiers complétés',
                    data: data.map((d: any) => d.completedDossiers),
                    borderColor: 'rgb(75, 192, 192)',
                    backgroundColor: 'rgba(75, 192, 192, 0.2)',
                    tension: 0.1,
                    fill: true
                  },
                  {
                    label: 'Tâches génériques complétées',
                    data: data.map((d: any) => d.completedGeneric),
                    borderColor: 'rgb(153, 102, 255)',
                    backgroundColor: 'rgba(153, 102, 255, 0.2)',
                    tension: 0.1,
                    fill: true
                  },
                  {
                    label: 'Dossiers approuvés',
                    data: data.map((d: any) => d.approvedDossiers),
                    borderColor: 'rgb(54, 162, 235)',
                    backgroundColor: 'rgba(54, 162, 235, 0.2)',
                    tension: 0.1,
                    fill: true
                  },
                  {
                    label: 'Dossiers rejetés',
                    data: data.map((d: any) => d.rejectedDossiers),
                    borderColor: 'rgb(255, 99, 132)',
                    backgroundColor: 'rgba(255, 99, 132, 0.2)',
                    tension: 0.1,
                    fill: true
                  }
                ]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: 'top',
                  },
                  title: {
                    display: true,
                    text: `Performance sur les ${selectedPeriod.value} derniers jours`
                  }
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      stepSize: 1
                    }
                  }
                }
              }
            });
          }
        }

        // Recharger aussi les performances des agents
        loadSupervisorData();
        
      } catch (err: any) {
        console.error('Erreur chargement graphique:', err);
        error('Erreur lors du chargement du graphique');
      }
    };

    const validateTask = async (validation: any, decision: string) => {
      try {
        await ApiService.post(`/supervisor/validate-task/${validation.id}`, {
          decision,
          supervisorComment: decision === 'REJECTED' ? 'Nécessite des corrections' : 'Validé par le superviseur'
        });
        
        success(`Dossier ${decision === 'APPROVED' ? 'approuvé' : 'rejeté'} avec succès`);
        loadSupervisorData();
      } catch (err: any) {
        error(err.response?.data?.message || 'Erreur lors de la validation');
      }
    };

    const getAlertClass = (type: string): string => {
      const classes = {
        'warning': 'alert-warning',
        'error': 'alert-danger',
        'info': 'alert-info',
        'success': 'alert-success'
      };
      return classes[type] || 'alert-info';
    };

    const getAlertIcon = (type: string): string => {
      const icons = {
        'warning': 'flaticon-warning text-warning',
        'error': 'flaticon-error text-danger',
        'info': 'flaticon-info text-info',
        'success': 'flaticon-check text-success'
      };
      return icons[type] || 'flaticon-info text-info';
    };

    const getPriorityBadge = (priority: string): string => {
      const badges = {
        'LOW': '<span class="badge bg-success">Faible</span>',
        'NORMAL': '<span class="badge bg-info">Normale</span>',
        'HIGH': '<span class="badge bg-warning">Élevée</span>',
        'CRITICAL': '<span class="badge bg-danger">Critique</span>'
      };
      return badges[priority] || `<span class="badge bg-secondary">${priority}</span>`;
    };

    const formatDate = (dateString: string): string => {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    onMounted(() => {
      loadSupervisorData();
      loadPerformanceData();
    });

    return {
      loading,
      selectedPeriod,
      periodOptions,
      performanceChart,
      stats,
      agentPerformances,
      alerts,
      loadSupervisorData,
      loadPerformanceData,
      validateTask,
      getAlertClass,
      getAlertIcon,
      getPriorityBadge,
      formatDate
    };
  }
});
</script>

<style scoped>
.performance-chart {
  height: 300px;
}

.alert-item {
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 0.375rem;
  border: 1px solid transparent;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffecb5;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c2c7;
}

.alert-info {
  background-color: #d1ecf1;
  border-color: #b8daff;
}

.alert-success {
  background-color: #d4edda;
  border-color: #c3e6cb;
}

.agent-avatar {
  width: 40px;
  height: 40px;
  background: #f8f9fa;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #6c757d;
}

.progress {
  min-width: 100px;
}

.performance-chart {
  height: 300px;
}
</style>